import React, { ReactNode, useContext, useState, useEffect } from "react";
import { Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
import { WIZARD_ACTION } from "types";
import ImportAlert, { ImportAlertProps } from "components/importer/ImportAlert";
import useAlerts from "./useImportAlerts";
import { ScrollableFormContainer } from "components/layout/ScrollableContainer";
import {
  TableType,
  useDefaultTableOptions,
} from "hooks/useDefaultTableOptions";
import {
  useMaterialReactTable,
  MRT_ColumnDef,
  MaterialReactTable,
  MRT_TableInstance,
} from "material-react-table";

export interface CorrectionFormProps<T extends object> {
  entity: T;
  setEntity: (entity: T) => void;
}

export interface PreviewStepProps<T extends object> {
  CorrectionForm: React.FC<CorrectionFormProps<T>>;
  columns: MRT_ColumnDef<T>[];
  maxHeight: number | string;
}

const PreviewStep = <T extends object>({
  CorrectionForm,
  columns,
  maxHeight,
}: PreviewStepProps<T>): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(ImportWizardContext);
  const [topAlerts, setTopAlerts] = useState<ImportAlertProps[]>([]);
  const ALERTS = useAlerts({
    discardedRows: state.discardedRows.length,
    successfulImports: state.csvData.length - state.discardedRows.length,
    entityName: state.entityName,
  });

  useEffect(() => {
    if (!state.failedImports.length && state.discardedRows.length) {
      setTopAlerts([ALERTS.DUPLICATE_RECORDS]);
    } else if (state.failedImports.length) {
      setTopAlerts([
        {
          severity: "warning",
          disableClose: true,
          title: t("failedImports.title", {
            count: state.failedImports.length,
            entity: t(`${state.entityName}.label_plural`, { ns: "entities" }),
            ns: "importWizard",
          }),
          paragraphs: [
            t("failedImports.row_error", {
              ns: "importWizard",
              row:
                state.failedImports[0].index + (state.csvUsesTemplate ? 1 : 2),
              error: state.failedImports[0].error,
            }),
          ],
        },
      ]);
    } else {
      setTopAlerts([]);
    }
  }, [state.discardedRows, state.failedImports]);

  const handleSetFailedImport = (entity: T) => {
    dispatch({
      type: WIZARD_ACTION.UPDATE_FAILED_IMPORT,
      payload: entity,
    });
  };

  const tableDefaults = useDefaultTableOptions<T>(TableType.SIMPLE);
  const table = useMaterialReactTable<T>({
    ...tableDefaults,
    data: state.previewEntities,
    columns,
    enableSorting: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: "compact",
    },
    state: {
      isLoading: false,
      showLoadingOverlay: false,
    },
    // getRowId: (row, index) => index.toString(),
  }) as MRT_TableInstance<T>;

  return (
    <Stack direction="column" spacing={0} sx={{ height: maxHeight }}>
      {topAlerts.map((alert) => (
        <ImportAlert key={alert.title} {...alert} />
      ))}
      {state.failedImports.length === 0 ? (
        <>
          <Stack direction="row" spacing={2} pb={2} alignItems="center">
            <Typography variant="h6" gutterBottom>
              {t("preview_title", {
                ns: "importWizard",
              })}
            </Typography>
            <Typography variant="body1">
              {t("total_records", {
                count: state.modifiedData.length - state.discardedRows.length,
                ns: "importWizard",
              })}
            </Typography>
            <Typography variant="body1">
              {t("duplicate_records", {
                count: state.discardedRows.length,
                ns: "importWizard",
              })}
            </Typography>
          </Stack>
          <ScrollableFormContainer maxHeight={`calc(${maxHeight} - 20px)`}>
            <MaterialReactTable table={table} />
          </ScrollableFormContainer>
        </>
      ) : (
        <CorrectionForm
          entity={state.failedImport}
          setEntity={handleSetFailedImport}
        />
      )}
    </Stack>
  );
};

export default PreviewStep;
